/**
 * Ortak kullanılan yardımcı fonksiyonları barındıran yardımcı modül
 */

/**
 * Video ID'lerini normalize eder, farklı formatların eşleşmesini kolaylaştırır
 * Frontend ve backend arasında format uyumsuzluğuna karşı
 * 
 * @param videoId Video ID
 * @returns Normalize edilmiş video ID
 */
export function normalizeVideoId(videoId?: string): string {
  if (!videoId) return '';
  // Tüm boşlukları, tireleri ve alt çizgileri kaldır
  return videoId.replace(/[\s\-_]/g, '').toLowerCase();
}

/**
 * Metin önizlemesi oluşturma
 * 
 * @param text Orijinal metin
 * @param maxLength Maksimum karakter sayısı
 * @returns Kısaltılmış metin
 */
export function getTextPreview(text: string, maxLength: number = 250): string {
  if (!text) return '';
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength) + '...';
}

/**
 * İki video ID'sinin normalize edilmiş hallerini karşılaştırır
 * 
 * @param id1 Birinci video ID
 * @param id2 İkinci video ID
 * @returns Eğer ID'ler eşleşiyorsa true, aksi halde false
 */
export function videoIdsMatch(id1?: string, id2?: string): boolean {
  return normalizeVideoId(id1) === normalizeVideoId(id2);
}

/**
 * Hata mesajı formatını standartlaştırır
 * 
 * @param error Hata objesi veya mesajı
 * @returns Formatlı hata mesajı
 */
export function formatErrorMessage(error: unknown): string {
  if (error instanceof Error) return error.message;
  if (typeof error === 'string') return error;
  return 'Bilinmeyen hata oluştu';
} 

// Video ID'den SEO-friendly slug oluşturma
export function createVideoSlug(title: string, videoId: string): string {
  if (!title) return videoId;
  
  const slug = title
    .toLowerCase()
    // Türkçe karakterleri İngilizce karşılıklarına çevir
    .replace(/ğ/g, 'g')
    .replace(/ü/g, 'u')
    .replace(/ş/g, 's')
    .replace(/ı/g, 'i')
    .replace(/ö/g, 'o')
    .replace(/ç/g, 'c')
    .replace(/[^a-z0-9\s-]/g, '') // Diğer özel karakterleri kaldır
    .replace(/\s+/g, '-') // Boşlukları tire ile değiştir
    .replace(/-+/g, '-') // Çoklu tireleri tek tire yap
    .trim();
  
  return `${slug}---${videoId}`;
}

// SEO-friendly video URL oluşturma
export function createVideoUrl(title: string, videoId: string, language: string = 'tr'): string {
  const slug = createVideoSlug(title, videoId);
  return `/video/${slug}/${language}`;
}

// Slug'dan video ID çıkarma
export function extractVideoIdFromSlug(slug: string): string {
  // Eğer slug zaten YouTube ID formatındaysa (11 karakter)
  if (slug && slug.length === 11 && /^[a-zA-Z0-9_-]+$/.test(slug)) {
    return slug;
  }
  
  // --- ayırıcısından sonraki kısmı al
  const parts = slug.split('---');
  if (parts.length >= 2) {
    return parts[parts.length - 1]; // Son parça
  }
  
  // Fallback: Eski yöntem
  const youtubeIdPattern = /[a-zA-Z0-9_-]{11}/g;
  const matches = slug.match(youtubeIdPattern);
  
  if (matches && matches.length > 0) {
    return matches[matches.length - 1];
  }
  
  return slug; // Fallback olarak slug'ı döndür
} 

// Kanal isminden slug oluşturma
export function createChannelSlug(title: string): string {
  if (!title) return '';
  
  return title
    .toLowerCase()
    // Türkçe karakterleri İngilizce karşılıklarına çevir
    .replace(/ğ/g, 'g')
    .replace(/ü/g, 'u')
    .replace(/ş/g, 's')
    .replace(/ı/g, 'i')
    .replace(/ö/g, 'o')
    .replace(/ç/g, 'c')
    .replace(/[^a-z0-9\s-]/g, '') // Diğer özel karakterleri kaldır
    .replace(/\s+/g, '-') // Boşlukları tire ile değiştir
    .replace(/-+/g, '-') // Çoklu tireleri tek tire yap
    .trim();
}

// SEO-friendly kanal URL oluşturma
export function createChannelUrl(title: string, channelId: string): string {
  const slug = createChannelSlug(title);
  return slug ? `/channel/${slug}/${channelId}` : `/channel/${channelId}`;
}

// Slug'dan kanal ID çıkarma
export function extractChannelIdFromSlug(slug: string): string {
  // Eğer slug zaten YouTube channel ID formatındaysa (UC ile başlayan)
  if (slug && slug.startsWith('UC') && slug.length > 20) {
    return slug;
  }
  
  // Eğer slug/channelId formatındaysa
  const parts = slug.split('/');
  if (parts.length >= 2) {
    const channelId = parts[parts.length - 1]; // Son parça
    if (channelId.startsWith('UC') && channelId.length > 20) {
      return channelId;
    }
  }
  
  return slug; // Fallback olarak slug'ı döndür
} 